/**
 * Runtime Google Tag Manager bootstrap.
 *
 * The tracking IDs live in app_settings (admin portal → Settings → Marketing)
 * and arrive with GET /api/settings, so GTM cannot sit statically in
 * index.html — it is injected here once the settings resolve. Everything a
 * GTM tag needs beyond the container (GA4 / Meta / TikTok / Clarity IDs,
 * brand, environment) is pushed into the dataLayer BEFORE gtm.js loads, so
 * the container's Data Layer Variables resolve at load time and one container
 * schema serves every brand.
 *
 * Framework-free on purpose: no React, no store imports — callable from
 * anywhere, testable in isolation.
 */

export type MarketingIds = {
	gtm_container_id?: string | null
	ga4_measurement_id?: string | null
	meta_pixel_id?: string | null
	tiktok_pixel_id?: string | null
	clarity_project_id?: string | null
}

export type TrackParams = Record<string, string | number | boolean | null | undefined>

declare global {
	interface Window {
		dataLayer?: Array<Record<string, unknown>>
	}
}

/** One-shot latch. A settings refetch or HMR remount must not re-inject. */
let injected = false

const GTM_SRC = 'https://www.googletagmanager.com/gtm.js'

/**
 * Push an event for GTM. Safe before — or entirely without — GTM: pushes land
 * in a plain array that GTM drains when (if) it loads, so call sites never
 * need to know whether tracking is enabled.
 */
export const trackEvent = (event: string, params?: TrackParams): void => {
	window.dataLayer = window.dataLayer || []
	window.dataLayer.push({ event, ...params })
}

/**
 * Not env-build based: the Plus fork serves staging and production from one
 * build and tells them apart by hostname (see its index.html), so hostname is
 * the one signal that works identically in both forks.
 */
const resolveEnvironment = (): string => {
	if (/^stag\./i.test(window.location.hostname)) return 'staging'
	return process.env.NODE_ENV === 'production' ? 'production' : 'development'
}

/**
 * Inject GTM once. No-ops when the container ID is blank (tracking off for
 * this brand) or in embed mode (admin impersonation sessions must never leak
 * into marketing data).
 */
export const initMarketingTags = (ids: MarketingIds, ctx: { brand: string; embed: boolean }): void => {
	const containerId = ids.gtm_container_id?.trim()
	if (!containerId || ctx.embed || injected) return
	// Belt and braces for dev HMR, where module state resets but the DOM keeps
	// the script from the previous module instance.
	if (document.querySelector(`script[src^="${GTM_SRC}"]`)) return
	injected = true

	window.dataLayer = window.dataLayer || []
	// Context first, so the container's {{DLV}}s resolve during load.
	window.dataLayer.push({
		ga4_measurement_id: ids.ga4_measurement_id?.trim() || null,
		meta_pixel_id: ids.meta_pixel_id?.trim() || null,
		tiktok_pixel_id: ids.tiktok_pixel_id?.trim() || null,
		clarity_project_id: ids.clarity_project_id?.trim() || null,
		brand: ctx.brand,
		environment: resolveEnvironment(),
		embed: false,
	})
	window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' })

	const script = document.createElement('script')
	script.async = true
	script.src = `${GTM_SRC}?id=${encodeURIComponent(containerId)}`
	document.head.appendChild(script)
}
